/**
 * The contest Results page's rules (Super-admin/SetupBracket.js, #223), kept pure so they can be
 * tested on their own.
 *
 * A Manager scores each team, then publishes the results. Publishing is what the server calls
 * completing the match: usp_SetPlayerMatchScore with isCompleted true locks every score, stamps the
 * contest's result as final and lets the prize pool be paid by the scores, and none of it can be
 * undone. It used to be a "Match Completed" checkbox shown only in the last team's score dialog, so
 * a Manager who scored every team never completed the contest. The checkbox's state also outlived
 * the dialog, so ticking it, closing the dialog and then saving another team's score silently
 * completed the contest with teams still unscored. A score save now never completes it; only
 * publishRequest does.
 */

import { parseServerUtc } from './lotClock';

/** What the API sends for a contest with no match date: DateTime.MinValue. */
const NO_DATE_MS = parseServerUtc('0001-01-01T00:00:00');

/** The server's own sentence for a lock-in with a team unscored (usp_SetPlayerMatchScore). */
export const UNSCORED_TEAM_MESSAGE = 'Enter a score for every team before publishing the results.';
export const ALREADY_PUBLISHED_MESSAGE = 'The results have already been published.';
export const NO_MATCH_DATE_MESSAGE = 'Set the match start date before publishing the results.';
export const MATCH_NOT_STARTED_MESSAGE = 'The results can be published once the match has started.';
/** #269: said here, before the request - the server's own sentence for it is ungrammatical. */
export const SCORE_NO_MATCH_DATE_MESSAGE =
    'Score cannot be updated because the match is not scheduled yet. Set the match start date first.';

/**
 * @param {object} row A row of Tournament/GetTournamentTeamMatch.
 * @returns {boolean} Whether the contest's match date has been set (every row carries the same one).
 */
export const isMatchDateSet = (row) => {
    const ms = parseServerUtc(row?.matchStartDate);
    return Number.isFinite(ms) && ms > NO_DATE_MS;
};

/**
 * @param {object} row A row of Tournament/GetTournamentTeamMatch.
 * @returns {boolean} Whether the team has a score. It is null until scored; 0 is a score.
 */
export const isScored = (row) => Number.isInteger(row?.playerScore);

/**
 * @param {Array} rows The contest's rows.
 * @returns {boolean} Whether the results have been published. The server completes every row of a
 *     contest at once, so any one row says it.
 */
export const isResultsPublished = (rows) => Array.isArray(rows) && rows.some((row) => row?.isCompleted === true);

/**
 * Why a team's score cannot be saved yet (#269): usp_SetPlayerMatchScore refuses a score until the
 * contest has a match date.
 *
 * @param {object} row The team's row of Tournament/GetTournamentTeamMatch.
 * @returns {string|null} The reason, or null when the score can be sent.
 */
export const scoreBlocker = (row) => (isMatchDateSet(row) ? null : SCORE_NO_MATCH_DATE_MESSAGE);

/**
 * Why the results cannot be published yet, checked in the order the Manager has to act.
 *
 * @param {Array} rows The contest's rows.
 * @param {number} nowMs The current time, epoch milliseconds.
 * @returns {string|null} The reason, or null when they can be published.
 */
export const publishBlocker = (rows, nowMs) => {
    if (!Array.isArray(rows) || rows.length === 0) return UNSCORED_TEAM_MESSAGE;
    if (isResultsPublished(rows)) return ALREADY_PUBLISHED_MESSAGE;
    if (!isMatchDateSet(rows[0])) return NO_MATCH_DATE_MESSAGE;
    if (nowMs < parseServerUtc(rows[0].matchStartDate)) return MATCH_NOT_STARTED_MESSAGE;
    if (!rows.every(isScored)) return UNSCORED_TEAM_MESSAGE;
    return null;
};

/**
 * The final standings the confirmation shows: highest score first, as usp_UpdateNoBracketDetails
 * ranks them. Two teams of a contest cannot share a score; the match id only keeps the order fixed.
 *
 * @param {Array} rows The contest's rows.
 * @returns {Array} A sorted copy.
 */
export const rankTeams = (rows) =>
    [...(rows ?? [])].sort((a, b) =>
        (b?.playerScore ?? -Infinity) - (a?.playerScore ?? -Infinity) || (a?.matchId ?? 0) - (b?.matchId ?? 0));

/**
 * The body of a score save. It never completes the contest.
 *
 * @param {object} row The team's row.
 * @param {number} score The score entered.
 * @returns {object} The Tournament/UpdatePlayerMatchScore body.
 */
export const scoreRequest = (row, score) => ({
    matchId: row?.matchId,
    tournamentId: row?.tournamentId,
    playerScore: score,
    isCompleted: false,
});

/**
 * The body that publishes the results: the first team's own score sent again, with isCompleted.
 * The server's same-score check skips the row being written, so the unchanged score passes. Build
 * it from rows just read, so a score changed elsewhere since the page loaded is not put back.
 *
 * @param {Array} rows The contest's rows.
 * @returns {object} The Tournament/UpdatePlayerMatchScore body.
 */
export const publishRequest = (rows) => ({
    matchId: rows?.[0]?.matchId,
    tournamentId: rows?.[0]?.tournamentId,
    playerScore: rows?.[0]?.playerScore,
    isCompleted: true,
});
